import { cn } from "@design-system/utils";
import { SearchIcon } from "lucide-react";
import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  ChangeEvent,
} from "react";
import { useDebouncedCallback } from "use-debounce";

import { Input, InputProps } from "../input";

export interface SearchInputProps extends InputProps {
  debounceMs: number;
}

export function SearchInput({
  value,
  onChange,
  debounceMs,
  className,
  ...props
}: SearchInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const reportedValue = useRef(value);

  const reportChange = useDebouncedCallback((nextValue: string) => {
    reportedValue.current = nextValue;

    const syntheticEvent = {
      target: { value: nextValue },
      currentTarget: { value: nextValue },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  }, debounceMs);

  useEffect(() => {
    if (value === reportedValue.current) {
      return;
    }

    reportedValue.current = value;
    reportChange.cancel();
    setInputValue(value);
  }, [value, reportChange]);

  const handleSetInputValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      reportChange(e.target.value);
    },
    [reportChange]
  );

  return (
    <div className={cn("relative", className)}>
      <SearchIcon
        className="text-shape-neutral-outlined-foreground-muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        aria-hidden
      />
      <Input
        {...props}
        className="pl-9"
        value={inputValue}
        onChange={handleSetInputValue}
      />
    </div>
  );
}
